import { CONTENT_TYPES, TIERS, type ContentType, type Tier } from '../../lib/keywordSweep';

export type TierFilter = 'all' | Tier;
export type TypeFilter = 'all' | 'money' | ContentType;

interface ChipProps {
  active: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}

/** The pill Ideation uses for its kind filters, so a filter looks the same on every screen. */
export function FilterChip({ active, onClick, title, children }: ChipProps) {
  return (
    <button
      type="button"
      className="pressable"
      aria-pressed={active}
      onClick={onClick}
      title={title}
      style={{
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-hairline)'}`,
        background: active ? 'var(--accent-soft)' : 'var(--surface-card)',
        color: active ? 'var(--accent-text)' : 'var(--text-muted)',
        borderRadius: 'var(--radius-pill)',
        padding: '3px 10px',
        fontSize: 'var(--text-2xs)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

const divider = <span aria-hidden style={{ width: '1px', height: '16px', background: 'var(--border-hairline)' }} />;

interface QueueFiltersProps {
  tier: TierFilter;
  onTier: (tier: TierFilter) => void;
  type: TypeFilter;
  onType: (type: TypeFilter) => void;
  /** Counts over the rows the status toggle lets through. */
  tierCounts: Record<Tier, number>;
  typeCounts: Record<ContentType, number>;
  /** Rows tied to an offer, plus every landing page: the money pages. */
  moneyCount: number;
  showClosed: boolean;
  onShowClosed: (show: boolean) => void;
  closedCount: number;
  /** A column sort is on: offer the way back to the hand-set order. */
  sorted: boolean;
  onQueueOrder: () => void;
}

export default function QueueFilters({
  tier,
  onTier,
  type,
  onType,
  tierCounts,
  typeCounts,
  moneyCount,
  showClosed,
  onShowClosed,
  closedCount,
  sorted,
  onQueueOrder,
}: QueueFiltersProps) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-3)' }}>
      <FilterChip active={tier === 'all'} onClick={() => onTier('all')}>
        All tiers
      </FilterChip>
      {(Object.keys(TIERS) as Tier[]).map((t) => (
        <FilterChip key={t} active={tier === t} onClick={() => onTier(tier === t ? 'all' : t)} title={TIERS[t].blurb}>
          {t} {tierCounts[t]}
        </FilterChip>
      ))}
      {divider}
      <FilterChip active={type === 'all'} onClick={() => onType('all')}>
        All types
      </FilterChip>
      <FilterChip active={type === 'money'} onClick={() => onType(type === 'money' ? 'all' : 'money')} title="Landing pages and every row tied to an offer">
        Money pages {moneyCount}
      </FilterChip>
      {(Object.keys(CONTENT_TYPES) as ContentType[]).map((t) => (
        <FilterChip key={t} active={type === t} onClick={() => onType(type === t ? 'all' : t)}>
          {CONTENT_TYPES[t].label} {typeCounts[t]}
        </FilterChip>
      ))}
      {divider}
      <FilterChip active={showClosed} onClick={() => onShowClosed(!showClosed)} title="Denied and published entries are hidden by default">
        {showClosed ? 'Hide' : 'Show'} denied & published ({closedCount})
      </FilterChip>
      {sorted ? (
        <span style={{ marginLeft: 'auto' }}>
          <FilterChip active={false} onClick={onQueueOrder} title="Back to the hand-set order, where rows can be dragged">
            ↺ Queue order
          </FilterChip>
        </span>
      ) : null}
    </div>
  );
}
